import { useRef, useState, useEffect } from 'react';
import { IoIosArrowBack, IoIosArrowForward } from 'react-icons/io';
import './../scss/trust-badge.style.scss'
import { useTranslation } from 'react-i18next';
import { reviews } from './../constant/trust-pilot-badge.constant';

const TrustpilotBadge = () => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const { t } = useTranslation();
    const [isReadMore, setIsReadMore] = useState(true);
    const isMobile = window.innerWidth < 576;
    const toggleReadMore = () => setIsReadMore(prev => !prev);
    const isLaptop = window.innerWidth >= 576 && window.innerWidth < 1440;
    const infiniteReviews = [...reviews, ...reviews, ...reviews];
    const originalLength = reviews.length;
    const centerStartIndex = originalLength;

    // Touch handling state for swipe gestures
    const [touchStart, setTouchStart] = useState<number | null>(null);
    const [touchEnd, setTouchEnd] = useState<number | null>(null);

    // Minimum swipe distance (in px)
    const minSwipeDistance = 50;

    const onTouchStart = (e: React.TouchEvent) => {
        setTouchEnd(null); // otherwise the swipe is fired even with usual touch events
        setTouchStart(e.targetTouches[0].clientX);
    };

    const onTouchMove = (e: React.TouchEvent) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;
        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        if (isLeftSwipe) {
            handleInfiniteScroll('next');
        }
        if (isRightSwipe) {
            handleInfiniteScroll('prev');
        }
    };

    const scrollToIndex = (index: number) => {
        if (containerRef.current) {
            const cardWidth = 402 + 24; // card + gap
            containerRef.current.scrollTo({
                left: index * cardWidth,
                behavior: 'smooth',
            });
            setCurrentIndex(index);
        }
    };

    const handleInfiniteScroll = (direction: 'next' | 'prev') => {
        if (containerRef.current) {
            const cardWidth = 402 + 24;
            let newIndex;

            if (direction === 'next') {
                newIndex = currentIndex + 1;
                if (newIndex >= infiniteReviews.length) {
                    newIndex = centerStartIndex;
                }
            } else {
                newIndex = currentIndex - 1;
                if (newIndex < 0) {
                    newIndex = centerStartIndex + originalLength - 1;
                }
            }

            containerRef.current.scrollTo({
                left: newIndex * cardWidth,
                behavior: 'smooth',
            });
            setCurrentIndex(newIndex);
        }
    };

    useEffect(() => {
        if (containerRef.current) {
            const cardWidth = 402 + 24;
            const initialIndex = centerStartIndex;
            containerRef.current.scrollTo({
                left: initialIndex * cardWidth,
                behavior: 'auto',
            });
            setCurrentIndex(initialIndex);
        }
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            if (containerRef.current) {
                const scrollLeft = containerRef.current.scrollLeft;
                const cardWidth = 402 + 24;
                const index = Math.round(scrollLeft / cardWidth);
                setCurrentIndex(index);

                if (index <= 0) {
                    const newIndex = centerStartIndex + originalLength - 1;
                    containerRef.current.scrollTo({
                        left: newIndex * cardWidth,
                        behavior: 'auto',
                    });
                    setCurrentIndex(newIndex);
                } else if (index >= infiniteReviews.length - 1) {
                    const newIndex = centerStartIndex;
                    containerRef.current.scrollTo({
                        left: newIndex * cardWidth,
                        behavior: 'auto',
                    });
                    setCurrentIndex(newIndex);
                }
            }
        };

        const container = containerRef.current;
        if (container) container.addEventListener('scroll', handleScroll);
        return () => {
            if (container) container.removeEventListener('scroll', handleScroll);
        };
    }, [infiniteReviews.length, centerStartIndex, originalLength]);

    return (
        <section className="trust-badge-section px-3 padding-56-hear-from-our-clients">
            <div className=" d-flex flex-column align-items-start align-items-sm-center justify-content-start justify-content-sm-center">
                <h2 className="trust-badge-title text-start text-sm-center pb-0 mb-0 ">
                    {t('HEAR_FROM_OUR_CLIENTS')}
                </h2>
                <h6 className={`text-start text-sm-center trust-badge-description ${isLaptop ? ' w-100' : ' w-sm-100 w-50'}   padding-bottom-74 pt-3 mt-0 mb-0  pb-5 `}>
                    {t('HEAR_DIRECTLY_FROM_CUSTOMERS')}
                    {t('DISCOVER_HOW_WE_MADE_A_DIFFERENCE')}
                </h6>
            </div>

            {/* Arrows */}
            <button
                className='margin-inside-responsive chervon-right-icon trust-badge-arrow d-sm-block d-none trust-badge-arrow-left '
                onClick={() => handleInfiniteScroll('prev')}
            >
                <div className='d-flex'>
                    <IoIosArrowBack />
                </div>
            </button>

            <button
                className='margin-inside-responsive chervon-left-icon d-sm-block d-none trust-badge-arrow trust-badge-arrow-right'
                onClick={() => handleInfiniteScroll('next')}
            >
                <div className='d-flex'>
                    <IoIosArrowForward />
                </div>
            </button>

            {/* Carousel Cards */}
            <div className="trust-badge-carousel-wrapper">
                <div className="fade-scroll-wrapper">
                    <div className="fade-overlay fade-left" />
                    <div className="fade-overlay fade-right" />

                    <div
                        className="margin-inside trust-badge-carousel"
                        ref={containerRef}
                        onTouchStart={onTouchStart}
                        onTouchMove={onTouchMove}
                        onTouchEnd={onTouchEnd}
                    >

                        {infiniteReviews.map((review, idx) => (
                            <div
                                key={`${review.id}-${Math.floor(idx / originalLength)}`}
                                className="trust-badge-card"
                            >
                                <h5 className="trust-badge-card-title mb-0">
                                    {t(review.title)}
                                    {!isReadMore || isMobile ? t(review.readMore) : '...'}
                                    {!isMobile && (
                                        <button onClick={toggleReadMore} className=" px-1 ms-2 read-more-button">
                                            {isReadMore ? t('Show More') : t('Show Less')}
                                        </button>
                                    )}

                                </h5>
                                <div className="trust-badge-card-author-wrapper mt-4 pt-1">
                                    <img
                                        src={review.avatar}
                                        alt=""
                                        className="trust-badge-avatar"
                                    />
                                    <div className="trust-badge-author-details">
                                        <strong className="trust-badge-author-name">
                                            {t(review.author)}
                                            <div onClick={() => window.open(review.linkedInUrl, "_blank")} className='cursor-pointer mb-2'>
                                                <svg
                                                    width="13"
                                                    height="13"
                                                    viewBox="0 0 13 13"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        d="M12.0377 0H0.959766C0.429102 0 0 0.418945 0 0.936914V12.0605C0 12.5785 0.429102 13 0.959766 13H12.0377C12.5684 13 13 12.5785 13 12.0631V0.936914C13 0.418945 12.5684 0 12.0377 0ZM3.85684 11.0779H1.92715V4.87246H3.85684V11.0779ZM2.89199 4.02695C2.27246 4.02695 1.77227 3.52676 1.77227 2.90977C1.77227 2.29277 2.27246 1.79258 2.89199 1.79258C3.50898 1.79258 4.00918 2.29277 4.00918 2.90977C4.00918 3.52422 3.50898 4.02695 2.89199 4.02695ZM11.0779 11.0779H9.15078V8.06152C9.15078 7.34297 9.13809 6.41621 8.14785 6.41621C7.14492 6.41621 6.99258 7.20078 6.99258 8.01074V11.0779H5.06797V4.87246H6.91641V5.72051H6.9418C7.19824 5.23301 7.82793 4.71758 8.76484 4.71758C10.7174 4.71758 11.0779 6.00234 11.0779 7.67305V11.0779Z"
                                                        fill="#1567C0"
                                                    />
                                                </svg>
                                            </div>
                                        </strong>
                                        <strong className="trust-badge-author-designation">
                                            {t(review.designation)}
                                        </strong>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="trust-badge-pagination" style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '24px' }}>
                        {Array.from({ length: originalLength }).map((_, i) => {
                            const displayIndex = (currentIndex - centerStartIndex + originalLength) % originalLength;
                            const isActive = i === displayIndex;

                            return (
                                <div
                                    key={i}
                                    onClick={() => scrollToIndex(centerStartIndex + i)}
                                    style={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        backgroundColor: isActive ? '#7F56D9' : '#D9D9D9',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease',
                                    }}
                                />
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default TrustpilotBadge;
